import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

export default function TeamDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { team_id } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadTeam = async () => {
      try {
        const res = await fetch("/competition/web/structure/");
        const json = await res.json();
        setData(Array.isArray(json) ? json : []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadTeam();
  }, [team_id]);

  const teamData = useMemo(() => {
    const stateTeam = location.state?.team;

    if (stateTeam && String(stateTeam.id) === String(team_id)) {
      return {
        ...stateTeam,
        players: [...(stateTeam.players || [])].sort(
          (a, b) => (a.jersey_number || 0) - (b.jersey_number || 0)
        ),
      };
    }

    for (const category of data) {
      for (const team of category.teams || []) {
        if (String(team.id) === String(team_id)) {
          return {
            ...team,
            category_name: category.name,
            players: [...(team.players || [])].sort(
              (a, b) => (a.jersey_number || 0) - (b.jersey_number || 0)
            ),
          };
        }
      }
    }

    return null;
  }, [data, team_id, location.state]);

  if (loading) {
    return (
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          background:
            "linear-gradient(180deg,#f8f8f9 0%,#ffffff 30%,#f9f9fa 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2.2}>
            <Skeleton variant="rounded" width={150} height={42} />
            <Skeleton variant="rounded" width="100%" height={320} />
            <Skeleton variant="rounded" width="100%" height={300} />
            <Skeleton variant="rounded" width="100%" height={360} />
          </Stack>
        </Container>
      </Box>
    );
  }

  if (!teamData) {
    return (
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          background:
            "linear-gradient(180deg,#f8f8f9 0%,#ffffff 30%,#f9f9fa 100%)",
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={2.2}>
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackRoundedIcon />}
              sx={{
                alignSelf: "flex-start",
                height: 42,
                px: 2.2,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 800,
              }}
              variant="outlined"
            >
              Back
            </Button>

            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: "22px", md: "28px" },
                border: "1.6px solid",
                borderColor: "primary.main",
                background: "linear-gradient(180deg,#ffffff 0%,#fbfbfc 100%)",
                px: { xs: 2, md: 3 },
                py: { xs: 4, md: 5 },
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  fontWeight: 800,
                  color: "primary.main",
                  mb: 1,
                }}
              >
                Team not found
              </Typography>

              <Typography sx={{ color: "text.secondary" }}>
                We could not find the requested information.
              </Typography>
            </Paper>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: { xs: 3, md: 5 },
        background:
          "linear-gradient(180deg,#f8f8f9 0%,#ffffff 30%,#f9f9fa 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={{ xs: 2.2, md: 3 }}>
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              alignSelf: "flex-start",
              height: 42,
              px: 2.2,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 800,
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "rgba(0,0,0,0.02)",
              },
            }}
            variant="outlined"
          >
            Back
          </Button>

          {teamData.team_photo && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: "22px", sm: "26px", md: "30px" },
                border: "1.6px solid",
                borderColor: "primary.main",
                background: "linear-gradient(180deg, #ffffff 0%, #fbfbfc 100%)",
                boxShadow: "0 16px 34px rgba(0,0,0,0.08)",
                overflow: "hidden",
                p: { xs: 1.4, sm: 2.2, md: 3 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "fit-content",
                  maxWidth: "100%",
                  borderRadius: { xs: "18px", sm: "22px", md: "24px" },
                  border: "1.6px solid #000",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                }}
              >
                <Box
                  component="img"
                  src={teamData.team_photo}
                  alt={`${teamData.name} team`}
                  sx={{
                    display: "block",
                    width: "auto",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: { xs: 280, sm: 360, md: 460 },
                  }}
                />
              </Box>
            </Paper>
          )}
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: "24px", md: "30px" },
              border: "1.6px solid",
              borderColor: "primary.main",
              background: "linear-gradient(180deg,#ffffff 0%,#fbfbfc 100%)",
              overflow: "hidden",
              boxShadow: "0 18px 44px rgba(0,0,0,.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems="center"
              spacing={{ xs: 2.5, md: 4 }}
              sx={{
                px: { xs: 2, sm: 2.5, md: 4 },
                py: { xs: 2.2, md: 3.5 },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", md: 320 },
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={teamData.logo || ""}
                  alt={teamData.name}
                  sx={{
                    width: "100%",
                    maxWidth: { xs: 240, sm: 280, md: 320 },
                    height: { xs: 190, sm: 230, md: 280 },
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Box>

              <Stack
                spacing={1.3}
                sx={{
                  width: "100%",
                  textAlign: { xs: "center", md: "left" },
                  alignItems: { xs: "center", md: "flex-start" },
                }}
              >
                <Chip
                  label={teamData.category_name}
                  sx={{
                    height: 34,
                    px: 1,
                    borderRadius: "999px",
                    backgroundColor: "primary.main",
                    color: "#fff",
                    fontWeight: 800,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: { xs: "1.8rem", sm: "2.1rem", md: "2.7rem" },
                    lineHeight: 1.02,
                    fontWeight: 800,
                    color: "primary.main",
                    wordBreak: "break-word",
                  }}
                >
                  {teamData.name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: ".95rem", md: "1rem" },
                    color: "text.secondary",
                    maxWidth: 720,
                  }}
                >
                  Meet the official roster of players representing {teamData.name} at SAAC Basketball Quito 2026.
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: "22px", md: "28px" },
              border: "1.6px solid",
              borderColor: "primary.main",
              background: "linear-gradient(180deg,#ffffff 0%,#fbfbfc 100%)",
              overflow: "hidden",
              boxShadow: "0 18px 44px rgba(0,0,0,.05)",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                pt: { xs: 2, md: 2.5 },
                pb: { xs: 1.2, md: 1.5 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.45rem" },
                  fontWeight: 800,
                  color: "text.primary",
                }}
              >
                Player Roster
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: ".9rem", md: ".95rem" },
                  color: "text.secondary",
                  mt: 0.4,
                }}
              >
                Registered squad for this competition.
              </Typography>
            </Box>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 320 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        backgroundColor: "primary.main",
                        color: "#fff",
                        fontWeight: 800,
                        borderBottom: "none",
                        py: 1.6,
                      },
                    }}
                  >
                    <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                      Jersey
                    </TableCell>
                    <TableCell>Player</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {teamData.players.length ? (
                    teamData.players.map((player) => (
                      <TableRow
                        key={player.id}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0,0,0,.018)",
                          },
                          "& td": {
                            borderBottomColor: "rgba(0,0,0,.06)",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            fontSize: { xs: ".95rem", md: "1rem" },
                          }}
                        >
                          #{player.jersey_number}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "text.primary",
                            fontSize: { xs: ".94rem", md: "1rem" },
                          }}
                        >
                          {player.name}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          py: 4,
                          textAlign: "center",
                          color: "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        No players registered for this team.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}